import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

/**
 * User management — list, create, edit, password-reset.
 *
 * Roles: ADMIN, PAYROLL_OFFICER, HR_MANAGER, FINANCE_HEAD, EMPLOYEE.
 * EMPLOYEE accounts are linked to an Employee row; non-employee accounts
 * (admin/HR/payroll/finance) are standalone.
 */

export type UserRole =
  | 'ADMIN'
  | 'PAYROLL_OFFICER'
  | 'HR_MANAGER'
  | 'FINANCE_HEAD'
  | 'EMPLOYEE';

export const ROLE_LABELS: Record<UserRole, string> = {
  ADMIN:           'Admin',
  PAYROLL_OFFICER: 'Payroll Officer',
  HR_MANAGER:      'HR Manager',
  FINANCE_HEAD:    'Finance Head',
  EMPLOYEE:        'Employee',
};

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  ADMIN:           'Full access. Can manage users, configure payroll, run/approve cycles.',
  PAYROLL_OFFICER: 'Configure payroll, manage employees, run payroll cycles.',
  HR_MANAGER:      'Manage employees and HR data; cannot run payroll.',
  FINANCE_HEAD:    'View payroll, approve runs; cannot edit employees.',
  EMPLOYEE:        'Self-service portal only. Cannot view other employees.',
};

export type User = {
  id: string;
  username: string;
  full_name: string;
  email: string;
  role: UserRole;
  is_active: boolean;
  created_at: string;
};

export type UserCreate = {
  username: string;
  full_name: string;
  email: string;
  role: Exclude<UserRole, 'EMPLOYEE'>;
  password: string;
};

export type UserPatch = {
  full_name?: string;
  email?: string;
  role?: UserRole;
  is_active?: boolean;
};

export function useUsers(includeInactive = false) {
  return useQuery({
    queryKey: ['users', { includeInactive }],
    queryFn: () => api.get<User[]>('/users',
      includeInactive ? { include_inactive: true } : undefined),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UserCreate) => api.post<User>('/users', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function usePatchUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: UserPatch }) =>
      api.patch<User>(`/users/${vars.id}`, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: (vars: { id: string; new_password?: string }) =>
      api.post<{ user_id: string; username: string; new_password: string }>(
        `/users/${vars.id}/reset-password`,
        { new_password: vars.new_password ?? null },
      ),
  });
}

/** Create a login account for an existing employee — uses the existing
 *  /auth/create-employee-user endpoint. */
export function useCreateEmployeeUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { employee_id: string; username: string; password?: string }) =>
      api.post<User>('/auth/create-employee-user', {
        employee_id: vars.employee_id,
        username: vars.username,
        password: vars.password ?? 'Employee@123',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}
